import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { isFromApp, isLoginRedirectSuppressed } from '@/biz/tool';

const PUBLIC_PATHS = [
  '/login',
  '/signup',
  '/forgot-password',
  '/clerk',
  '/email-unsubscribe',
  '/email-confirmed',
  '/campaign/invites',
  '/download'
];

const isPublicPath = (pathname: string, search: string) => {
  if (pathname === '/') {
    return Boolean(new URLSearchParams(search).get('id')?.trim());
  }
  if (pathname === '/download' || pathname.startsWith('/download/')) {
    return true;
  }
  if (PUBLIC_PATHS.some((p) => p === pathname)) return true;
  if (pathname.startsWith('/app-auth/')) return true;
  if (pathname === '/auth/bridge') return isFromApp();
  if (pathname.startsWith('/auth/') && pathname !== '/auth/bridge') return true;
  if (pathname === '/campaign/error') return true;
  // 邀请列表页免登录查看
  if (pathname === '/campaign/invites') return true;
  // 详情页免登录查看
  if (pathname.startsWith('/campaign/invites/detail/')) return true;
  if (pathname.startsWith('/campaign/detail/')) return true;
  // 达人排行榜公开可看；个人报告仍走登录态
  if (pathname === '/leaderboard') return true;
  // Opportunities：仅开放链接带 id 时可免登录
  if (pathname === '/deals/opportunities') {
    const q = search.startsWith('?') ? search.slice(1) : search;
    const id = new URLSearchParams(q).get('id');
    return Boolean(id?.trim());
  }
  return false;
};

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * 未登录时跳转到 /login，并携带当前路径为 redirect 参数
 * 需要重定向时不渲染子组件，避免先闪一下受保护页面再跳转
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const OAUTH_IN_PROGRESS_KEY = '__oauth_redirect_in_progress__';
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const oauthInProgress =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(OAUTH_IN_PROGRESS_KEY) === '1';

  const fromApp = isFromApp();
  const skipLoginRedirect = fromApp && isLoginRedirectSuppressed();
  const protectedPath = !isPublicPath(location.pathname, location.search);
  const waitingForAppSession =
    isLoaded && !isSignedIn && skipLoginRedirect && protectedPath;

  const needRedirect =
    isLoaded &&
    !isSignedIn &&
    !oauthInProgress &&
    !skipLoginRedirect &&
    protectedPath;

  React.useEffect(() => {
    if (!needRedirect) return;

    const redirect =
      location.pathname + (location.search ? location.search : '');
    if (fromApp) {
      const appParams = new URLSearchParams({
        from: 'app',
        redirect
      });
      navigate('/login?' + appParams.toString(), { replace: true });
      return;
    }
    navigate('/login?redirect=' + encodeURIComponent(redirect), {
      replace: true
    });
  }, [fromApp, needRedirect, location.pathname, location.search, navigate]);

  // 需要重定向时不渲染子组件，避免退出登录后页面闪烁
  if (needRedirect) {
    return null;
  }

  if (waitingForAppSession) {
    return null;
  }

  return <>{children}</>;
};

export default AuthGuard;
